import { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Save, X, Trash2, Edit as EditIcon, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyFaq = { question: '', answer: '', order: 100, published: true };

export const FaqAdminTab = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/faqs`, { withCredentials: true });
      setFaqs(data);
    } catch {
      toast.error('Failed to load FAQs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ ...emptyFaq, order: (faqs[faqs.length - 1]?.order ?? 0) + 10 });
  const startEdit = (f) => setEditing({ ...f });
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!editing.question.trim() || !editing.answer.trim()) {
      toast.error('Question and answer are required');
      return;
    }
    setSaving(true);
    const payload = {
      question: editing.question.trim(),
      answer: editing.answer.trim(),
      order: Number(editing.order) || 0,
      published: !!editing.published
    };
    try {
      if (editing.id) {
        await axios.put(`${API}/admin/faqs/${editing.id}`, payload, { withCredentials: true });
        toast.success('FAQ updated');
      } else {
        await axios.post(`${API}/admin/faqs`, payload, { withCredentials: true });
        toast.success('FAQ created');
      }
      setEditing(null);
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to save FAQ');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/admin/faqs/${deleteTarget.id}`, { withCredentials: true });
      toast.success('FAQ deleted');
      setDeleteTarget(null);
      await load();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const reorder = async (faq, delta) => {
    const newOrder = (faq.order || 0) + delta * 10;
    try {
      await axios.put(`${API}/admin/faqs/${faq.id}`, { order: newOrder }, { withCredentials: true });
      await load();
    } catch {
      toast.error('Failed to reorder');
    }
  };

  if (editing) {
    return (
      <div className="rounded-md border border-[#E5E0D8] p-5 md:p-8" style={{ backgroundColor: '#FFFFFF' }} data-testid="faq-editor">
        <div className="flex items-center justify-between mb-6">
          <h3 style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600, fontSize: '24px', color: '#1A1A1A' }}>
            {editing.id ? 'Edit FAQ' : 'New FAQ'}
          </h3>
          <Button variant="ghost" size="sm" onClick={cancel} style={{ fontFamily: 'Manrope' }} data-testid="faq-editor-cancel">
            <X size={16} className="mr-1" /> Cancel
          </Button>
        </div>

        <div className="space-y-4">
          <div>
            <label style={{ fontFamily: 'Manrope', fontSize: '13px', fontWeight: 600, color: '#5C5C5C' }} className="block mb-2">Question *</label>
            <input
              type="text"
              value={editing.question || ''}
              onChange={(e) => setEditing({ ...editing, question: e.target.value })}
              placeholder="e.g. Is it legal to sell food from home in Australia?"
              className="w-full px-4 py-2 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
              style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
              data-testid="faq-input-question"
            />
          </div>
          <div>
            <label style={{ fontFamily: 'Manrope', fontSize: '13px', fontWeight: 600, color: '#5C5C5C' }} className="block mb-2">Answer *</label>
            <textarea
              value={editing.answer || ''}
              onChange={(e) => setEditing({ ...editing, answer: e.target.value })}
              rows={6}
              placeholder="Clear, concise answer. This appears in the FAQ accordion + JSON-LD schema for AI search."
              className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
              style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0', lineHeight: 1.6 }}
              data-testid="faq-input-answer"
            />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label style={{ fontFamily: 'Manrope', fontSize: '13px', fontWeight: 600, color: '#5C5C5C' }} className="block mb-2">Sort order</label>
              <input
                type="number"
                value={editing.order ?? 0}
                onChange={(e) => setEditing({ ...editing, order: e.target.value })}
                className="w-full px-4 py-2 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
                style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
                data-testid="faq-input-order"
              />
            </div>
            <div className="flex items-end gap-2 pb-2">
              <input
                id="faq-published"
                type="checkbox"
                checked={!!editing.published}
                onChange={(e) => setEditing({ ...editing, published: e.target.checked })}
                data-testid="faq-input-published"
              />
              <label htmlFor="faq-published" style={{ fontFamily: 'Manrope', fontSize: '14px', color: '#3D3D3D' }}>
                Published (visible to visitors)
              </label>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-8">
          <Button variant="outline" onClick={cancel} style={{ fontFamily: 'Manrope' }}>Cancel</Button>
          <Button
            onClick={save}
            disabled={saving}
            className="rounded-md"
            style={{ backgroundColor: '#2E5C41', fontFamily: 'Manrope', fontWeight: 600 }}
            data-testid="faq-editor-save"
          >
            <Save size={14} className="mr-1" /> {saving ? 'Saving…' : 'Save FAQ'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="faq-admin-list">
      <div className="flex items-center justify-between mb-4">
        <p style={{ fontFamily: 'Manrope', color: '#5C5C5C', fontSize: '14px' }}>
          {loading ? 'Loading…' : `${faqs.length} FAQ${faqs.length === 1 ? '' : 's'} total`}
        </p>
        <Button
          onClick={startNew}
          className="rounded-md"
          style={{ backgroundColor: '#D95A41', fontFamily: 'Manrope', fontWeight: 600 }}
          data-testid="faq-new-btn"
        >
          <Plus size={16} className="mr-1" /> New FAQ
        </Button>
      </div>

      {faqs.length === 0 && !loading ? (
        <p className="text-center py-12" style={{ fontFamily: 'Manrope', color: '#5C5C5C' }}>No FAQs yet.</p>
      ) : (
        <div className="space-y-3">
          {faqs.map((f, idx) => (
            <div
              key={f.id}
              className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-4 md:p-5 rounded-md border border-[#E5E0D8]"
              style={{ backgroundColor: '#FFFFFF' }}
              data-testid={`faq-row-${f.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className="uppercase px-2 py-0.5 rounded"
                    style={{
                      fontFamily: 'Manrope', fontSize: '10px', fontWeight: 700, letterSpacing: '0.12em',
                      backgroundColor: f.published ? '#E5EFE4' : '#F2E4C9',
                      color: f.published ? '#2E5C41' : '#7A5A0F'
                    }}
                  >
                    {f.published ? 'Published' : 'Hidden'}
                  </span>
                  <span style={{ fontFamily: 'Manrope', fontSize: '12px', color: '#8A8073' }}>#{f.order}</span>
                </div>
                <div style={{ fontFamily: 'Manrope', fontWeight: 600, color: '#1A1A1A' }} className="mb-1">
                  {f.question}
                </div>
                <p style={{ fontFamily: 'Manrope', fontSize: '13px', color: '#5C5C5C' }} className="line-clamp-2">
                  {f.answer}
                </p>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <Button
                  variant="ghost" size="sm"
                  onClick={() => reorder(f, -1)}
                  disabled={idx === 0}
                  data-testid={`faq-up-${f.id}`}
                  title="Move up"
                >
                  <ArrowUp size={14} strokeWidth={1.5} />
                </Button>
                <Button
                  variant="ghost" size="sm"
                  onClick={() => reorder(f, 1)}
                  disabled={idx === faqs.length - 1}
                  data-testid={`faq-down-${f.id}`}
                  title="Move down"
                >
                  <ArrowDown size={14} strokeWidth={1.5} />
                </Button>
                <Button
                  variant="outline" size="sm" className="rounded-md"
                  onClick={() => startEdit(f)}
                  style={{ borderColor: '#D95A41', color: '#D95A41', fontFamily: 'Manrope', fontWeight: 600, fontSize: '13px' }}
                  data-testid={`faq-edit-${f.id}`}
                >
                  <EditIcon size={13} className="mr-1" /> Edit
                </Button>
                <Button
                  variant="outline" size="sm" className="rounded-md"
                  onClick={() => setDeleteTarget(f)}
                  style={{ borderColor: '#991B1B', color: '#991B1B', fontFamily: 'Manrope', fontWeight: 600, fontSize: '13px' }}
                  data-testid={`faq-delete-${f.id}`}
                >
                  <Trash2 size={13} className="mr-1" /> Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="faq-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600 }}>
              Delete this FAQ?
            </AlertDialogTitle>
            <AlertDialogDescription style={{ fontFamily: 'Manrope' }}>
              "{deleteTarget?.question}" — this cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel style={{ fontFamily: 'Manrope' }}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              style={{ backgroundColor: '#991B1B', fontFamily: 'Manrope', fontWeight: 600 }}
              data-testid="faq-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
